import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle, Mail, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const MAX_DAILY_EMAILS = 100;

export default function SharingTreeEmailBatchModal({
  mode,
  year,
  recipientIds = [],
  period = null,
  onClose,
  onComplete,
}) {
  const isSignupNotice = mode === "signup_open";
  const [usage, setUsage] = useState(null);
  const [signupPeriod, setSignupPeriod] = useState(period);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [batchSize, setBatchSize] = useState(25);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    async function loadInfo() {
      setLoading(true);
      setError("");
      try {
        const requests = [api.get("/sharing-tree/email-usage")];
        if (isSignupNotice && !period) {
          requests.push(api.get(`/sharing-tree/period?year=${year}`));
        }
        const responses = await Promise.all(requests);
        if (!mounted) return;
        setUsage(responses[0].data);
        if (responses[1]) setSignupPeriod(responses[1].data);
      } catch (err) {
        if (mounted) setError(formatApiError(err));
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadInfo();
    return () => { mounted = false; };
  }, [isSignupNotice, period, year]);

  const dailyRemaining = usage?.daily_remaining ?? 0;
  const maxBatch = Math.min(MAX_DAILY_EMAILS, dailyRemaining);
  const selectedCount = recipientIds.length;
  const remainingCount = lastResult ? lastResult.remaining : selectedCount;
  const canSend = !loading
    && !sending
    && selectedCount > 0
    && dailyRemaining > 0
    && (!isSignupNotice || signupPeriod?.is_open);

  const description = isSignupNotice
    ? `Notify ${selectedCount} selected client${selectedCount !== 1 ? "s" : ""} that Sharing Tree signup is open.`
    : `Send pickup reminders to ${selectedCount} selected enrolled household${selectedCount !== 1 ? "s" : ""}.`;

  const periodDetails = useMemo(() => {
    const details = isSignupNotice ? signupPeriod : period;
    if (!details) return null;
    if (isSignupNotice) {
      return (
        <>
          <div><strong>Signup window:</strong> {details.open_date || "—"} → {details.close_date || "—"}</div>
          <div><strong>Program year:</strong> {year}</div>
        </>
      );
    }
    return (
      <>
        <div><strong>Distribution date:</strong> {details.distribution_date || "—"}</div>
        {details.distribution_time && <div><strong>Time:</strong> {details.distribution_time}</div>}
        {(details.location_name || details.location_address) && (
          <div><strong>Location:</strong> {[details.location_name, details.location_address].filter(Boolean).join(", ")}</div>
        )}
      </>
    );
  }, [isSignupNotice, period, signupPeriod, year]);

  async function sendBatch() {
    if (!canSend) return;
    const requestedBatchSize = Math.max(1, Math.min(Number(batchSize) || 1, maxBatch));
    setSending(true);
    setError("");
    try {
      const payload = isSignupNotice
        ? { client_ids: recipientIds, year, batch_size: requestedBatchSize }
        : { household_ids: recipientIds, year, batch_size: requestedBatchSize };
      const endpoint = isSignupNotice
        ? "/sharing-tree/send-signup-open-emails"
        : "/sharing-tree/send-reminder-batch";
      const { data } = await api.post(endpoint, payload);
      setLastResult(data);
      setUsage({
        date: data.date,
        daily_limit: data.daily_limit,
        daily_used: data.daily_used,
        daily_remaining: data.daily_remaining,
      });
      onComplete?.(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.42)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 sm:p-7 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="sharing-tree-email-batch-modal"
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: "var(--fb-primary)" }}>
              <Mail className="w-4 h-4" />
              Sharing Tree email
            </div>
            <h3 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>
              {isSignupNotice ? "Notify clients signup is open" : "Send pickup reminders"}
            </h3>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm mb-4" style={{ color: "var(--fb-text-muted)" }}>{description}</p>

        {periodDetails && (
          <div className="rounded-xl p-4 mb-4 text-sm space-y-1.5" style={{ background: "var(--fb-bg)", border: "1px solid var(--fb-border)" }}>
            {periodDetails}
          </div>
        )}

        <div className="rounded-xl p-4 mb-4" style={{ background: "#EFF6FF", border: "1px solid #BFDBFE" }}>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="font-semibold" style={{ color: "#1E3A8A" }}>Daily transactional email allowance</span>
            <span className="font-bold tabular-nums" style={{ color: "#1D4ED8" }}>
              {loading ? "…" : `${dailyRemaining} of ${usage?.daily_limit || MAX_DAILY_EMAILS} remaining`}
            </span>
          </div>
          <p className="text-xs mt-1" style={{ color: "#1D4ED8" }}>
            All Sharing Tree email actions share this 100-email daily limit.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="email-batch-size" className="block text-sm font-semibold mb-1.5">Emails in this batch</label>
            <input
              id="email-batch-size"
              data-testid="email-batch-size"
              type="number"
              min="1"
              max={maxBatch || 1}
              value={batchSize}
              onChange={(e) => setBatchSize(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-lg border outline-none focus:border-[#052F66]"
              style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
              disabled={loading || sending || maxBatch === 0}
            />
            <p className="text-xs mt-1" style={{ color: "var(--fb-text-muted)" }}>
              Maximum available right now: {maxBatch || 0}. Successful sends are not repeated if you continue later.
            </p>
          </div>

          {isSignupNotice && signupPeriod && !signupPeriod.is_open && (
            <div className="fb-status-warning p-3 rounded-lg text-sm">
              Signup is not currently open for {year}. This action is disabled until the configured window begins.
            </div>
          )}

          {lastResult && (
            <div className="rounded-xl p-4 text-sm" style={{ background: "#ECFDF5", border: "1px solid #6EE7B7" }} data-testid="email-batch-result">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#059669" }} />
                <div className="space-y-1" style={{ color: "#065F46" }}>
                  <strong>Batch complete</strong>
                  <div>{lastResult.sent} sent · {lastResult.skipped} skipped without email · {lastResult.failed?.length || 0} failed</div>
                  {lastResult.already_sent > 0 && <div>{lastResult.already_sent} already completed and not re-sent.</div>}
                  {lastResult.in_progress > 0 && <div>{lastResult.in_progress} currently being handled elsewhere.</div>}
                  <div className="font-semibold">{lastResult.remaining} selected recipient{lastResult.remaining !== 1 ? "s" : ""} remaining</div>
                </div>
              </div>
            </div>
          )}

          {lastResult?.limit_reached && lastResult.remaining > 0 && (
            <div className="fb-status-warning p-3 rounded-lg text-sm">
              The shared daily limit has been reached. The remaining recipients can be sent after the allowance resets.
            </div>
          )}

          {error && <div className="fb-status-error p-3 rounded-lg text-sm" data-testid="email-batch-error">{error}</div>}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={sending}
            className="flex-1 px-4 py-2.5 rounded-xl border text-sm font-semibold disabled:opacity-50"
            style={{ borderColor: "var(--fb-border)" }}
          >
            {remainingCount === 0 ? "Close" : "Cancel"}
          </button>
          <button
            data-testid="send-email-batch-btn"
            onClick={sendBatch}
            disabled={!canSend || remainingCount === 0}
            className="flex-1 fb-btn-primary px-4 py-2.5 rounded-xl font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Mail className="w-4 h-4" />
            {sending ? "Sending…" : lastResult ? "Send next batch" : "Send batch"}
          </button>
        </div>
      </div>
    </div>
  );
}